import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, RefreshCw, Share2, Linkedin, Link2, MessageCircle, Check } from "lucide-react";
import { fetchBlogPost } from "@/lib/adminApi";
import { Overline } from "@/components/Motion";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { SITE } from "@/data/site";

const IMG_RE = /^https?:\/\/\S+\.(png|jpe?g|gif|webp|avif)(\?\S*)?$/i;

function renderBody(body) {
  const lines = (body || "").split("\n");
  const blocks = [];
  let list = [];
  let key = 0;

  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ul key={key++} className="list-disc list-inside space-y-2 text-brand-navy/85 font-light">
          {list.map((it, j) => <li key={j}>{it}</li>)}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith("## ")) {
      flushList();
      blocks.push(
        <h2 key={key++} className="font-serif text-3xl text-brand-navy mt-8">{line.slice(3)}</h2>
      );
    } else if (line.startsWith("- ")) {
      list.push(line.slice(2));
    } else if (IMG_RE.test(line)) {
      flushList();
      blocks.push(
        <img key={key++} src={line} alt="" className="rounded-2xl w-full my-6 border border-brand-border" />
      );
    } else {
      flushList();
      blocks.push(
        <p key={key++} className="text-brand-navy/85 font-light leading-relaxed">{line}</p>
      );
    }
  });
  flushList();
  return blocks;
}

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { dateStyle: "long" });
  } catch {
    return iso;
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPost(await fetchBlogPost(slug));
        setState("ok");
      } catch {
        setState("notfound");
      }
    })();
  }, [slug]);

  const shareUrl = post
    ? `${process.env.REACT_APP_BACKEND_URL}/api/share/blog/${post.slug}`
    : "";
  const shareText = post ? `${post.title} — Arivu Finserve` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press to copy the link.");
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-muted pt-24">
        <RefreshCw className="h-5 w-5 animate-spin mr-3" /> Loading…
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-bg pt-24 text-center px-6">
        <h1 className="font-serif text-4xl text-brand-navy">Post not found</h1>
        <Link to="/blog" className="mt-6 text-brand-green inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back to blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={`${post.title} | Arivu Finserve`}
        description={post.summary || post.title}
        image={post.cover}
        url={shareUrl}
      />

      <section className="relative pt-40 pb-12 overflow-hidden bg-brand-navy">
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" /> All posts
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-serif text-white text-4xl lg:text-6xl font-light leading-tight mt-6"
          >
            {post.title}
          </motion.h1>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-white/60 text-sm">
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {fmt(post.created_at)}</span>
            <span className="text-brand-accent">·</span>
            <span>By {post.author || "Avishaa Thakar"}</span>
          </div>
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {post.tags.map((t) => (
                <span key={t} className="text-xs bg-white/10 text-white/80 rounded-full px-3 py-1">{t}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {post.cover && (
        <div className="max-w-3xl mx-auto px-6 -mt-2">
          <img src={post.cover} alt="" className="rounded-2xl w-full h-72 lg:h-96 object-cover border border-brand-border" />
        </div>
      )}

      <section className="max-w-3xl mx-auto px-6 py-14 lg:py-20 space-y-5">
        {post.summary && (
          <p className="font-serif text-2xl text-brand-navy italic leading-relaxed border-l-4 border-brand-green pl-6">{post.summary}</p>
        )}
        {renderBody(post.body)}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-10">
        <div className="border-t border-brand-border pt-8 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-brand-muted text-sm mr-2">
            <Share2 className="h-4 w-4" /> Share this post
          </span>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
            className="inline-flex items-center gap-2 rounded-full border border-[#25D366] text-[#128C4B] hover:bg-[#25D366] hover:text-white text-sm font-medium px-4 py-2 transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-linkedin"
            className="inline-flex items-center gap-2 rounded-full border border-brand-border text-brand-navy hover:bg-brand-navy hover:text-white text-sm font-medium px-4 py-2 transition-colors"
          >
            <Linkedin className="h-4 w-4" /> LinkedIn
          </a>
          <button
            type="button"
            onClick={copyLink}
            data-testid="share-copy"
            className="inline-flex items-center gap-2 rounded-full border border-brand-border text-brand-navy hover:bg-brand-bg text-sm font-medium px-4 py-2 transition-colors"
          >
            {copied ? <Check className="h-4 w-4 text-brand-green" /> : <Link2 className="h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-24">
        <div className="bg-brand-navy rounded-3xl p-8 lg:p-12 text-center">
          <Overline className="text-brand-accent">Ready to plan?</Overline>
          <p className="text-white/70 font-light mt-3">Book a free 30-minute conversation with Avishaa.</p>
          <a
            href="https://calendly.com/arivufinserve/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 rounded-full bg-brand-accent text-white text-sm font-medium px-8 py-4 hover:bg-white hover:text-brand-navy transition-colors"
          >
            Book a call
          </a>
        </div>
      </section>
    </div>
  );
}
